/**
 * GREENWAY BIOTECH - UNIVERSAL QUICK DOSE BUTTON HANDLER v2.0
 * Structured data approach with product configurations
 * 
 * Supports data attributes:
 * - data-gal: Reservoir/container size in gallons
 * - data-fert: Fertilizer amount (lbs per 100 gal base rate)
 * - data-calmag: Cal-Mag amount (ml per 100 gal base rate)
 * - data-product: Product identifier for custom formatting (on container)
 * 
 * Version: 2.0.0
 */

(function() {
  'use strict';

  // ============================================================
  // CONFIGURATION
  // ============================================================

  const CONFIG = {
    // Conversion factors
    conversions: {
      gramsPerLb: 453.592,
      gramsPerOz: 28.3495,
      gramsPerTsp: 6,        // Approximate for most fertilizers
      gramsPerTbsp: 18,
      mlPerFlOz: 29.5735,
      mlPerTsp: 4.929,
      mlPerTbsp: 14.787
    },

    // Product-specific settings
    // Add new products here as needed
    products: {
      'lettuce-fertilizer': {
        name: 'Lettuce Fertilizer 8-15-36',
        fertDensity: 6,           // grams per tsp (for volume conversion)
        showCalMag: true,
        calMagUnit: 'ml',
        fertUnit: 'g',
        supplementName: 'Cal-Mag',
        supplementLink: 'https://www.greenwaybiotech.com/products/cal-mag-plus-2-0-0-concentrate'
      },
      'cucumber-fertilizer': {
        name: 'Cucumber Fertilizer 8-16-36',
        fertDensity: 5.6,
        showCalMag: true,
        calMagUnit: 'ml',
        fertUnit: 'g',
        supplementName: 'Cal-Mag',
        supplementLink: 'https://www.greenwaybiotech.com/products/cal-mag-plus-2-0-0-concentrate'
      },
      'tomato-fertilizer': {
        name: 'Tomato Fertilizer 4-18-38',
        fertDensity: 6,
        showCalMag: true,
        calMagUnit: 'ml',
        fertUnit: 'g',
        supplementName: 'Cal-Mag',
        supplementLink: 'https://www.greenwaybiotech.com/products/cal-mag-plus-2-0-0-concentrate'
      },
      'pepper-herb-fertilizer': {
        name: 'Pepper & Herb Fertilizer 11-11-40',
        fertDensity: 5.6,
        showCalMag: true,
        calMagUnit: 'ml',
        fertUnit: 'g',
        supplementName: 'Cal-Mag',
        supplementLink: 'https://www.greenwaybiotech.com/products/cal-mag-plus-2-0-0-concentrate'
      },
      'strawberry-fertilizer': {
        name: 'Strawberry Fertilizer 8-12-32',
        fertDensity: 5.6,
        showCalMag: true,
        calMagUnit: 'ml',
        fertUnit: 'g',
        supplementName: 'Cal-Mag',
        supplementLink: 'https://www.greenwaybiotech.com/products/cal-mag-plus-2-0-0-concentrate'
      },
      'calcium-nitrate': {
        name: 'Calcium Nitrate 15.5-0-0',
        fertDensity: 5.5,
        showCalMag: false,
        fertUnit: 'g',
        supplementName: null
      },
      'default': {
        name: 'Fertilizer',
        fertDensity: 6,
        showCalMag: true,
        calMagUnit: 'ml',
        fertUnit: 'g',
        supplementName: 'Cal-Mag',
        supplementLink: 'https://www.greenwaybiotech.com/products/cal-mag-plus-2-0-0-concentrate'
      }
    }
  };

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================

  /**
   * Format number with appropriate precision
   */
  function formatNumber(num, maxDecimals = 1) {
    if (num === 0) return '0';
    if (num >= 100) return Math.round(num).toLocaleString();
    if (num >= 10) return num.toFixed(1).replace(/\.0$/, '');
    if (num >= 1) return num.toFixed(1);
    return num.toFixed(2);
  }

  /**
   * Convert grams to best display unit
   */
  function gramsToDisplay(grams, density) {
    const tsp = grams / density;
    const tbsp = tsp / 3;
    const oz = grams / CONFIG.conversions.gramsPerOz;
    const lbs = grams / CONFIG.conversions.gramsPerLb;

    // Choose best unit based on amount
    if (grams < 3) {
      return `${formatNumber(grams)}g (~${formatNumber(tsp, 1)} tsp)`;
    } else if (grams < 15) {
      return `${formatNumber(grams)}g (~${formatNumber(tsp, 0)} tsp)`;
    } else if (grams < 50) {
      return `${formatNumber(grams)}g (~${formatNumber(tbsp, 0)} tbsp)`;
    } else if (grams < 200) {
      return `${formatNumber(grams)}g (~${formatNumber(oz, 1)} oz)`;
    } else {
      return `${formatNumber(grams)}g (~${formatNumber(oz, 0)} oz / ${formatNumber(lbs, 2)} lbs)`;
    }
  }

  /**
   * Convert ml to best display unit
   */
  function mlToDisplay(ml) {
    const tsp = ml / CONFIG.conversions.mlPerTsp;
    const tbsp = ml / CONFIG.conversions.mlPerTbsp;
    const floz = ml / CONFIG.conversions.mlPerFlOz;

    if (ml < 5) {
      return `${formatNumber(ml)}ml (~${formatNumber(tsp, 1)} tsp)`;
    } else if (ml < 30) {
      return `${formatNumber(ml)}ml (~${formatNumber(tsp, 0)} tsp)`;
    } else if (ml < 100) {
      return `${formatNumber(ml)}ml (~${formatNumber(tbsp, 0)} tbsp)`;
    } else {
      return `${formatNumber(ml)}ml (~${formatNumber(floz, 1)} fl oz)`;
    }
  }

  /**
   * Calculate fertilizer amount based on reservoir size
   * @param {number} gallons - Reservoir size
   * @param {number} lbsPer100Gal - Base rate in lbs per 100 gallons
   * @returns {number} - Grams needed
   */
  function calculateFertilizer(gallons, lbsPer100Gal) {
    const lbsNeeded = (gallons / 100) * lbsPer100Gal;
    return lbsNeeded * CONFIG.conversions.gramsPerLb;
  }

  /**
   * Calculate Cal-Mag amount based on reservoir size
   * @param {number} gallons - Reservoir size
   * @param {number} mlPer100Gal - Base rate in ml per 100 gallons
   * @returns {number} - ml needed
   */
  function calculateCalMag(gallons, mlPer100Gal) {
    return (gallons / 100) * mlPer100Gal;
  }

  /**
   * Generate display output for a dose
   */
  function generateOutput(gallons, fertGrams, calMagMl, productConfig) {
    const fertDisplay = gramsToDisplay(fertGrams, productConfig.fertDensity);
    
    let output = `<strong>${gallons} Gallons:</strong> ${fertDisplay}`;
    
    if (productConfig.showCalMag && calMagMl > 0) {
      const calMagDisplay = mlToDisplay(calMagMl);
      if (productConfig.supplementLink) {
        output += ` + <a href="${productConfig.supplementLink}" target="_blank" style="color: inherit; text-decoration: underline;">${productConfig.supplementName}</a> ${calMagDisplay}`;
      } else {
        output += ` + ${productConfig.supplementName} ${calMagDisplay}`;
      }
    }
    
    return output;
  }

  // ============================================================
  // MAIN INITIALIZATION
  // ============================================================

  function initQuickDose() {
    // Find all Quick Dose sections within .gwb-product containers
    const quickDoseSections = document.querySelectorAll('.gwb-product .quick-dose');
    
    quickDoseSections.forEach(section => {
      const buttons = section.querySelectorAll('.dose-btn');
      const display = section.querySelector('.dose-display');
      
      if (!buttons.length || !display) return;
      
      // Get product config from data attribute or use default
      const productId = section.getAttribute('data-product') || 'default';
      const productConfig = CONFIG.products[productId] || CONFIG.products['default'];
      
      // Add click handlers to each button
      buttons.forEach(button => {
        button.addEventListener('click', function() {
          // Remove active class from all buttons in this section
          buttons.forEach(btn => {
            btn.classList.remove('active');
            btn.setAttribute('aria-pressed', 'false');
          });
          
          // Add active class to clicked button
          this.classList.add('active');
          this.setAttribute('aria-pressed', 'true');
          
          // Get values from data attributes
          const gallons = parseFloat(this.getAttribute('data-gal'));
          const fertRate = parseFloat(this.getAttribute('data-fert')); // lbs per 100 gal
          const calMagRate = parseFloat(this.getAttribute('data-calmag')) || 0; // ml per 100 gal
          
          // Check for legacy data-info attribute (backward compatibility)
          const legacyInfo = this.getAttribute('data-info');
          
          if (legacyInfo) {
            // Use legacy string directly
            display.innerHTML = legacyInfo;
          } else if (!isNaN(gallons) && !isNaN(fertRate)) {
            // Calculate from structured data
            const fertGrams = calculateFertilizer(gallons, fertRate);
            const calMagMl = calculateCalMag(gallons, calMagRate);
            
            // Generate and display output
            display.innerHTML = generateOutput(gallons, fertGrams, calMagMl, productConfig);
          } else {
            display.innerHTML = '⚠️ Invalid dose configuration';
          }
        });
        
        // Keyboard accessibility
        button.addEventListener('keypress', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.click();
          }
        });
      });
    });
  }

  // ============================================================
  // INITIALIZATION
  // ============================================================

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuickDose);
  } else {
    initQuickDose();
  }

  // Expose for manual re-initialization if needed
  window.GWBQuickDose = {
    init: initQuickDose,
    config: CONFIG,
    version: '2.0.0'
  };

})();